import { styled } from '@linaria/react';
import { Badge, Card, Group } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import type { FloraPage } from '../../../../api/flora-pages-client/flora-pages-client.type';
import { FloraPageVisibility } from '../../../../api/flora-pages-client/flora-pages-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { FloraPageActionsMenu } from '../flora-page-actions-menu';
import { useFloraPageActions } from '../use-flora-page-actions';

type Props = {
  pages: FloraPage[];
};

/**
 * Card grid for the Pages gallery. Every card navigates to the viewer;
 * owner-only actions (share/unshare, delete) live in a per-card kebab menu
 * that is hidden for pages the current user does not own.
 *
 * @param {Props} props - The pages to render.
 * @returns {JSX.Element} The card grid.
 */
export function FloraPagesList({ pages }: Props) {
  const navigate = useNavigate();
  const {
    isOwner,
    toggleVisibility,
    pageToDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
    isDeleting,
    deleteError,
  } = useFloraPageActions();

  return (
    <>
      <PagesGrid>
        {pages.map((page) => {
          const isShared = page.visibility === FloraPageVisibility.Organization;
          return (
            <PageCard
              key={page.id}
              withBorder
              radius="md"
              padding="md"
              data-testid={`flora-page-card-${page.id}`}
              onClick={() => navigate(`/application/flora-agent/pages/${page.id}`)}
            >
              <Group justify="space-between" wrap="nowrap">
                <TitleLink
                  to={`/application/flora-agent/pages/${page.id}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <span aria-hidden="true">
                    <Icon name="article" size={20} />
                  </span>
                  <TruncatedTitle weight="medium">{page.title}</TruncatedTitle>
                </TitleLink>
                {isOwner(page) && (
                  <FloraPageActionsMenu
                    page={page}
                    onToggleVisibility={toggleVisibility}
                    onRequestDelete={requestDelete}
                  />
                )}
              </Group>
              <Group justify="space-between" mt="sm">
                <Badge variant="light" color={isShared ? 'green' : 'gray'}>
                  {isShared ? 'Organization' : 'Private'}
                </Badge>
                <Text size="small" color="var(--ink-light)">
                  {new Date(page.metadata_updated_at).toLocaleDateString()}
                </Text>
              </Group>
            </PageCard>
          );
        })}
      </PagesGrid>
      <DeleteConfirmationModal
        isOpen={!!pageToDelete}
        onClose={cancelDelete}
        onDelete={confirmDelete}
        title="Delete Page"
        itemName={pageToDelete ? pageToDelete.title : ''}
        isDeleting={isDeleting}
        error={deleteError}
      />
    </>
  );
}

const PagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const PageCard = styled(Card)`
  cursor: pointer;
`;

const TitleLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  color: inherit;
  text-decoration: none;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const TruncatedTitle = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
